import React, { useState } from 'react';
import { Award, X } from 'lucide-react';
import { mockBadges } from '../../services/mockData';

const BadgesCard = () => {
  const badges = mockBadges;
  const [selectedBadge, setSelectedBadge] = useState(null);

  const getRarityColor = (rarity) => {
    const colors = {
      common: 'bg-gray-100 text-gray-700 border-gray-300',
      uncommon: 'bg-green-100 text-green-700 border-green-300',
      rare: 'bg-blue-100 text-blue-700 border-blue-300',
      epic: 'bg-purple-100 text-purple-700 border-purple-300',
      legendary: 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    return colors[rarity] || colors.common;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <div className="card" data-testid="badges-card">
        <div className="card-header">
          <h2 className="card-title">Badges & Achievements</h2>
          <a href="/achievements" className="card-action">View All</a>
        </div>

        {badges.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏆</div>
            <p className="empty-state-text">No badges earned yet</p>
            <p className="text-xs text-gray-600 mt-2">Complete activities to earn your first badge!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {badges.map((badge) => (
              <button
                key={badge.badge_id}
                onClick={() => setSelectedBadge(badge)}
                className="flex flex-col items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer hover:scale-105 hover:shadow-md"
                data-testid={`badge-${badge.badge_id}`}
              >
                <div className="text-4xl mb-2">{badge.icon}</div>
                <p className="text-xs font-semibold text-gray-900 text-center mb-1">
                  {badge.name}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getRarityColor(badge.rarity)}`}>
                  {badge.rarity.charAt(0).toUpperCase() + badge.rarity.slice(1)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Total Badges Count */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award size={18} className="text-orange-500" />
              <span className="text-sm font-medium text-gray-700">Total Achievements</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{badges.length}</span>
          </div>
        </div>
      </div>

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedBadge(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Badge Details</h3>
              <button
                onClick={() => setSelectedBadge(null)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="text-7xl mb-4">{selectedBadge.icon}</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">{selectedBadge.name}</h4>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getRarityColor(selectedBadge.rarity)}`}>
                {selectedBadge.rarity.toUpperCase()}
              </span>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
                <p className="text-sm text-gray-600">{selectedBadge.description}</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1">Earned On</p>
                <p className="text-sm text-blue-700">{formatDate(selectedBadge.earned_date)}</p>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-900 mb-1">How to Earn More</p>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Complete daily check-ins</li>
                  <li>• Maintain conversation streaks</li>
                  <li>• Participate in group sessions</li>
                  <li>• Achieve real-world milestones</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setSelectedBadge(null)}
              className="w-full mt-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BadgesCard;
